import React, { useState, useMemo, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Button,
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
  styled,
  Collapse,
  IconButton,
  Popper,
  Popover,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material";
import _ from "lodash";
import { TopicChip as Chip } from '../../common/topicChip';
import { convertArrayToDictionary, defaultGuid } from "../../../../common/utils";
import AssignPrompts from "./assignPrompts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedPrompt, promptsSelector, setSelectedPromptData } from "../../../../store/slices/prompts";
import { companiesSelector } from "../../../../store/slices/company";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { PromptAssignmentChips, getPromptAssignment, useAssignmentsByPrompt } from "./promptAssignmentChips";


const PromptList = ({ data, setIsNew, setSelectedPrompt, selectedPrompt, scope, setScope, height, selectedCompany }) => {

  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [openAssign, setOpeAssign] = useState(false)
  const {selectedPromptData, prompts} = useSelector(promptsSelector)
  const selectedPromptMemo = useMemo(()=>selectedPromptData,[selectedPromptData]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filter, setFilter] = useState({archived: true, companySpecific: false});
  const { companies,  } = useSelector(companiesSelector);

  const company = useMemo(()=>{
    const dic = convertArrayToDictionary(companies, 'companyId');
    return dic?.[selectedCompany]
  },[companies, selectedCompany]);

  const assignmentsByPrompt = useAssignmentsByPrompt();


  const handleScopeChange = (event) => {
    setScope(event.target.value);
  };
  const handleNewPromptClick = () => {
    setIsNew(true);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const filteredData = useMemo(() => {
    return _.orderBy(data?.filter((item) => {
      const searchString = searchText.toLowerCase();
      
      // First apply search filter
      const matchesSearch = item.description?.toLowerCase()?.includes(searchString) ||
                           item.name?.toLowerCase()?.includes(searchString);
      
      if (!matchesSearch) return false;

      // Apply scope filtering
      // let scopeMatch = false;
      
      // if (scope === "Company") {
        
      //   scopeMatch = item.companyId !== "00000000-0000-0000-0000-000000000000";
      // }
      // else if (scope === "Company-Assigned") {
      //   scopeMatch = item.isProcessPrompt && _.includes(assignCategories, item.type);
      // }
      // // else if (scope === "Global") {
      // //   scopeMatch = item.companyId === "00000000-0000-0000-0000-000000000000";
      // // }
      // else {
      //   scopeMatch = true; 
      // }
      
      // if (!scopeMatch) return false;
      
     
      // if (filter.archived && item.status===3) {
      //   return false; 
      // }
      
      // if (filter.companySpecific) {
      //   return false; 
      // }
      if(item?.status===3 && filter.archived){
        return false;
      }
      if(filter.companySpecific){
        if(item?.assignCompanyId === defaultGuid|| item?.assignCompanyId=== null){
          return false;
        }
      }
      return true;
    }), ['name']);
  }, [data, searchText, scope, filter]);

  const groupedData = useMemo(() => {
    const result = {};

    // Sort by type, subType, and name
    const sortedData = _.sortBy(filteredData, ['type', 'subType', 'name']);

    // Group by type
    sortedData.forEach((item) => {
    
      let data = {...item}
      // if((item?.subType === 'Employee Name')&&(item?.name==='Inappropriate Instructions' || item?.name === 'Review Validation' || item?.name === 'PHI')){
      //   data = {...item, subType:item?.name}
      //  }
      
      if (!result[data.type]) {
        result[data.type] = {};
      }

      // Group by subType within each type
      const subTypeKey = data.subType || 'No SubType';
      if (!result[data.type][subTypeKey]) {
        result[data.type][subTypeKey] = [];
      }
      result[data.type][subTypeKey].push(data);
    });

    return result;
  }, [filteredData]);

  useEffect(() => {
    setSelectedPrompt(selectedPromptData);
    
    if (selectedPromptData) {
      Object.keys(groupedData).forEach((key, index) => {
        Object.values(groupedData[key]).forEach((items) => {
          items.forEach((item) => {
            if (item?.promptId === selectedPromptData?.promptId) {
              setExpandedIndex(index);
            }
          });
        });
      });
    }
  }, [ groupedData, selectedPromptData, setSelectedPrompt]);
  

const dispatch = useDispatch();
  const handleClick = (data) => {
    dispatch(setSelectedPromptData(data));
    setIsNew(false);
    setSelectedPrompt(data);
  };
 
  const toggleAccordion = (index) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  
  const handleFilterOpen = (event) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const clearFilters = () => {
    setFilter({archived: false, companySpecific: false});
  };

  const handleFilter = (event)=>{
      const{ name, checked } = event.target;
      setFilter((prevFilter) => ({
        ...prevFilter,
        [name]: checked,
      }));
  }
  
  
  return (
    <Box p={'16px 0'} className='dynamic-scroll-container' sx={{ height: `calc(100vh - ${height ? height : 200}px)` }}>
      <Box gap={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '53px', borderBottom: '1px solid #888', mt: '-16px', mb: 2}}>
        <Typography variant="body1" px={2}>List of Prompts</Typography>
        <div>
        {scope !== "Company-Assigned" ?
            <Tooltip title={company?.companyId ? "New Prompt" : "No Company Selected"} arrow disableInteractive>
              <IconButton
                color="primary"
                onClick={handleNewPromptClick}
                sx={{ 
                  border: '1px solid', 
                  borderColor: 'primary.main',
                  borderRadius: '4px',
                  width: '30px',
                  height: '30px'
                }}
                disabled = {!company?.companyId}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip> : null
            //   <Button
            //   // size="small"
            //   variant="contained"
            //   color="primary"
            //   onClick={()=>setOpeAssign(true)}
            //   sx={{ textTransform: "none" }}
            // >
            //   Assign Prompt
            // </Button>
          } 

          <IconButton 
            onClick={handleFilterOpen} 
            aria-describedby={filterAnchorEl ? 'filter-popover' : undefined}
            color={filter.archived || filter.companySpecific ? 'primary' : 'default'}
            title="Filter prompts"
            sx={{ 
              border: '1px solid', 
              borderColor: 'primary.main',
              borderRadius: '4px',
              width: '30px',
              height: '30px',
              marginLeft: '8px',
              marginRight: '12px'
            }}            
          >
           <FilterAltIcon sx={{ fontSize: 20 }} />
          </IconButton>

           <Popover
                  id="filter-popover"
                  open={Boolean(filterAnchorEl)}
                  onClose={handleFilterClose}
                  anchorEl={filterAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
            <Box sx={{ p: 2, minWidth: 250, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* <Typography variant="h6" gutterBottom>
                Filter Prompts
              </Typography> */}
              
             <FormControlLabel 
               control={
                 <Checkbox 
                   checked={filter.archived} 
                   onChange={handleFilter} 
                   name="archived" 
                 />
               } 
               label="Exclude Archived" 
             />
             <FormControlLabel 
               control={
                 <Checkbox 
                   checked={filter.companySpecific} 
                   onChange={handleFilter} 
                   name="companySpecific" 
                 />
               } 
               label="Show Company Specific Only" 
             />
              {/* <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={clearFilters}>
                  Clear
                </Button>
                <Button size="small" variant="contained" onClick={handleFilterClose}>
                  Apply
                </Button>
              </Stack> */}
            </Box>
          </Popover>  
          </div>        
      </Box>

      <Grid container spacing={2} alignItems="center" justifyContent={'space-between'} sx={{ ml: '-4px', width: '100%' }}>
        <Grid item xs={6} sx={{ paddingLeft: '4px !important' }}>
          <FormControl fullWidth>
            <InputLabel>Scope</InputLabel>
            <Select
              size="small"
              fullWidth
              // sx={{ width: '250px' }}
              label="Scope"
              value={scope}
              onChange={handleScopeChange}
            >
              <MenuItem value="Global">Global Prompts</MenuItem>
              <MenuItem value="Company">Company - All Prompts</MenuItem>
              <MenuItem value="Company-Assigned">Company - Assigned Prompts</MenuItem>
            </Select>
          </FormControl>          
        </Grid>

        <Grid item xs={6}>
          <Box sx={{ display:'flex', alignItems: 'center',gap: 1 ,  mr:1}}>
          <TextField
            size="small"
            label="Search"
            variant="outlined"
            fullWidth
            value={searchText}
            onChange={handleSearchChange}
          />
          
          </Box>
        </Grid>
       
      </Grid>
      <Divider sx={{ mt: 1 }} />
      <Box className='scrollable' marginTop={1}>
        {Object.keys(groupedData).map((type, index) => (
          <>
          <Box
          key={index}
          sx={{
            borderRadius: "6px",
            fontWeight: 400,
            lineHeight: 2.5,
            margin:'4px 8px 4px 0'
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.075)" : "rgba(0, 0, 0, 0.05)",
              cursor: "pointer",
              transition: "background-color 0.3s",
              
            }}
            onClick={() => toggleAccordion(index)}
          >
            <Typography fontSize={'larger'}>
               <strong >{type}</strong>
            </Typography>
            <IconButton size="small">
              {expandedIndex === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedIndex === index}>
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
            {
              _.map(_.keys(groupedData[type]),(subType,index)=>(
                <>
                <div style={{width:'100%',padding: subType!=='No SubType' || (subType==='No SubType' && type === 'Review Request') ? '4px 4px':0}}>
                {subType!=='No SubType' || (subType==='No SubType' && type === 'Review Request') ?<Typography variant="subtitle2" sx={{ paddingLeft:2,background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : "#F8F9FC",width:'100%',py:1}}>{(subType==='No SubType' && type === 'Review Request') ? "ALL" : subType}</Typography>:null}
                {groupedData[type][subType].map((item, index) => {
              const assignment = getPromptAssignment(assignmentsByPrompt, item);
              const isArchived = item?.status === 3;
              return (
              <Box
                key={`${type}-${index}`}
                onClick={() => handleClick(item)}
                sx={{
                  background: selectedPrompt?.promptId === item.promptId  ? (theme.palette.mode === 'dark' ? theme.palette.secondary.dark[200] : "#eff8ff") + " !important" : theme.palette.background.paper,
                  borderLeft: selectedPrompt?.promptId === item.promptId  ? `5px solid ${theme.palette.primary.main} !important` : "5px solid transparent !important",
                  padding: "8px",
                  paddingLeft: "10px",
                  lineHeight: 2,
                  marginTop: "10px",
                  margin:'4px 0 !important',
                  cursor: "pointer",
                  transition: 'background-color 0.3s, border-left 0.5s ease-in-out',
                  boxShadow: selectedPrompt?.promptId === item.promptId  ? "0px 1px 3px #ddd" : null,
                  borderRadius:'8px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Box sx={{ display: 'flex' }}>
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        <div className="same-line">
                          <span className="dynamic-name">{item.name}</span>
                          <Typography className="text-muted version" style={{fontStyle:'normal !important'}}>
                            {item.version ? ` (v${item.version})` : ''}
                          </Typography>
                        </div>
                        </div>
                  </Box>
                  {item.isProcessPrompt &&
                    <Box>
                      <Tooltip title={item?.assignCompanyId===selectedCompany ? 'Assigned to this company': "Global Assigned"} arrow>
                        <Box>
                          <Chip
                            key={index}
                            sx={{ mr: 1, background: '#0d6efd', color: '#FFF' }}
                            // color={item.companyId === defaultGuid ? '#ffe680': 'auto'}
                            label={'A'}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  }
                </Stack>
                <span className="text-muted">
                  {item.description?.length > 50 ? (
                    <>{item.description.slice(0, 50)}...</>
                  ) : (
                    item.description
                  )}
                </span>
                <PromptAssignmentChips assignment={assignment} isArchived={isArchived} sx={{ mt: 0.5 }} />
              </Box>
              );
            })}
                </div>
                 {/* {_.size(_.keys(groupedData[type]))>1&&index!==(_.size(_.keys(groupedData[type]))-1)&&<Divider/>} */}
                </>
              ))
            }
            </Box>
          </Collapse>
        </Box>

        
          </>
        ))}
      </Box>
      {openAssign && <AssignPrompts open={openAssign} handleClose={() => setOpeAssign(false)} companyId={selectedCompany} />}
    </Box>
  );
};

export default PromptList;
